import { useState, useEffect } from 'react';
import { getExperimentationAnalytics } from '../../services/experimentationService';
import BarChart from '../charts/BarChart';

const ExperimentationTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [experimentData, setExperimentData] = useState<any>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await getExperimentationAnalytics(30);
        setExperimentData(data);
      } catch (error) {
        console.error('Error loading experimentation data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading experimentation analytics...</p>
      </div>
    );
  }

  if (!experimentData || experimentData.experiments.length === 0) {
    return (
      <div style={{ padding: '2rem' }}>
        <h1 style={{ marginBottom: '2rem', fontSize: '1.75rem', fontWeight: 'bold' }}>
          A/B Testing & Experimentation
        </h1>
        <div style={{
          backgroundColor: 'white',
          padding: '2rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          textAlign: 'center',
        }}>
          <p style={{ color: '#666' }}>No active experiments found.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem' }}>
      <h1 style={{ marginBottom: '2rem', fontSize: '1.75rem', fontWeight: 'bold' }}>
        A/B Testing & Experimentation
      </h1>

      {/* KPI Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem',
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Active Experiments</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {experimentData.activeExperiments}
          </h2>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Completed</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {experimentData.completedExperiments}
          </h2>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Total Experiments</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {experimentData.experiments.length}
          </h2>
        </div>
      </div>

      {/* Experiments List */}
      {experimentData.experiments.map((experiment: any, expIndex: number) => {
        const variantData = {
          labels: experiment.variants.map((v: any) => v.variantName),
          datasets: [
            {
              label: 'Conversion Rate (%)',
              data: experiment.variants.map((v: any) => v.conversionRate),
              backgroundColor: ['#2563eb', '#10b981', '#f59e0b', '#8b5cf6'],
            },
          ],
        };

        return (
          <div
            key={expIndex}
            style={{
              backgroundColor: 'white',
              padding: '1.5rem',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              marginBottom: '2rem',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0 }}>{experiment.experimentName}</h3>
              <span style={{
                padding: '0.25rem 0.5rem',
                borderRadius: '4px',
                backgroundColor: experiment.status === 'active' ? '#d1fae5' : '#e0e0e0',
                color: experiment.status === 'active' ? '#065f46' : '#666',
                fontSize: '0.875rem',
              }}>
                {experiment.status}
              </span>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>
                Statistical Significance: <strong>{experiment.statisticalSignificance.toFixed(1)}%</strong>
              </p>
              <p style={{ margin: '0.25rem 0', color: '#666', fontSize: '0.875rem' }}>
                Total Users: {experiment.totalUsers}
              </p>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <BarChart data={variantData} height={200} />
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Variant</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Users</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Conversions</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Conversion Rate</th>
                </tr>
              </thead>
              <tbody>
                {experiment.variants.map((variant: any, varIndex: number) => (
                  <tr key={varIndex} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '0.75rem' }}>{variant.variantName}</td>
                    <td style={{ padding: '0.75rem' }}>{variant.users}</td>
                    <td style={{ padding: '0.75rem' }}>{variant.conversions}</td>
                    <td style={{ padding: '0.75rem', color: variant.conversionRate > 10 ? '#10b981' : '#666' }}>
                      {variant.conversionRate.toFixed(2)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
};

export default ExperimentationTab;

